import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Mail } from "lucide-react";
import { toast } from "sonner";
import FileAttach, { AttachmentList } from "../components/FileAttach";
import { useAuth } from "../contexts/AuthContext";

export default function MessagesPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [box, setBox] = useState("inbox");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ to_user_ids: [], subject: "", body: "", attachments: [] });
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get(`/messages?box=${box}`).then(r=>setItems(r.data||[])); }, [box, open]);
  // Use role-aware /messages/contacts (works for every role including parent/student/teacher).
  useEffect(() => {
    api.get("/messages/contacts").then(r => setUsers(r.data || [])).catch(() => setUsers([]));
  }, [user?.role]);

  const send = async () => {
    if (!form.to_user_ids?.length) { toast.error(t("select_recipient") || "Please select a recipient"); return; }
    if (!form.subject?.trim() || !form.body?.trim()) { toast.error(t("subject_body_required") || "Subject and body are required"); return; }
    try {
      await api.post("/messages", form);
      toast.success(t("success")); setOpen(false); setForm({ to_user_ids: [], subject: "", body: "", attachments: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error") || "Error");
    }
  };

  const openMsg = async (m) => {
    setSelected(m);
    if (box === "inbox") await api.post(`/messages/${m.id}/read`);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-3xl font-semibold">{t("messages")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-msg-btn"><Plus className="w-4 h-4 me-2"/>{t("new_message")}</Button></DialogTrigger>
          <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>{t("new_message")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("to_recipients")}</Label>
                {users.length === 0 ? (
                  <div className="text-sm text-muted-foreground bg-muted/40 px-3 py-2 rounded-md" data-testid="msg-no-contacts">
                    {t("no_contacts_available") || "No contacts available to message"}
                  </div>
                ) : (
                  <Select value={form.to_user_ids[0] || ""} onValueChange={v => setForm({ ...form, to_user_ids: [v] })}>
                    <SelectTrigger data-testid="msg-recipient"><SelectValue placeholder={t("to_recipients")} /></SelectTrigger>
                    <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({t(u.role) || u.role})</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              <div><Label>{t("subject_field")}</Label><Input data-testid="msg-subject" value={form.subject} onChange={e=>setForm({...form, subject:e.target.value})}/></div>
              <div><Label>{t("body")}</Label><Textarea rows={5} value={form.body} onChange={e=>setForm({...form, body:e.target.value})}/></div>
              <div><Label>{t("attachments")}</Label><FileAttach value={form.attachments || []} onChange={(v) => setForm({...form, attachments: v})} testIdPrefix="msg-attach"/></div>
              <Button onClick={send} className="w-full" data-testid="msg-send">{t("save")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={box} onValueChange={setBox}>
        <TabsList><TabsTrigger value="inbox" data-testid="msg-inbox">{t("inbox")}</TabsTrigger><TabsTrigger value="sent" data-testid="msg-sent">{t("sent")}</TabsTrigger></TabsList>
        <TabsContent value={box}>
          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="border shadow-none lg:col-span-1 max-h-[70vh] overflow-y-auto divide-y divide-border">
              {items.map(m => (
                <button key={m.id} onClick={()=>openMsg(m)} className={`w-full text-start p-4 hover:bg-muted smooth ${selected?.id===m.id?"bg-muted":""}`} data-testid={`msg-${m.id}`}>
                  <div className="font-medium text-sm">{m.subject}</div>
                  <div className="text-xs text-primary/80 mt-0.5 truncate">
                    {box === "sent"
                      ? (m.to_names?.length ? `${t("to_recipients")}: ${m.to_names.join("، ")}` : "")
                      : [m.from_name, m.from_school_name].filter(Boolean).join(" · ")}
                  </div>
                  {box !== "sent" && m.from_children?.length > 0 && (
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{t("parent_of_label")}: {m.from_children.join("، ")}</div>
                  )}
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{m.body}</div>
                  <div className="text-xs text-muted-foreground/70 font-mono mt-1">{m.at?.slice(0,16)}</div>
                </button>
              ))}
              {items.length===0 && <div className="p-8 text-center text-muted-foreground text-sm">{t("no_data")}</div>}
            </Card>
            <Card className="border shadow-none lg:col-span-2 p-6 min-h-[40vh]">
              {selected ? (
                <div>
                  <h3 className="text-xl font-semibold mb-2">{selected.subject}</h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-1">
                    {box !== "sent" && selected.from_name && <span data-testid="msg-from">{t("sender_label")}: <b className="text-foreground">{selected.from_name}</b>{selected.from_role ? ` (${t(selected.from_role) || selected.from_role})` : ""}</span>}
                    {box !== "sent" && selected.from_school_name && <span data-testid="msg-school">{t("school")}: <b className="text-foreground">{selected.from_school_name}</b></span>}
                    {box !== "sent" && selected.from_children?.length > 0 && <span data-testid="msg-children">{t("parent_of_label")}: <b className="text-foreground">{selected.from_children.join("، ")}</b></span>}
                    {box === "sent" && selected.to_names?.length > 0 && <span>{t("to_recipients")}: <b className="text-foreground">{selected.to_names.join("، ")}</b></span>}
                  </div>
                  <div className="text-xs text-muted-foreground mb-4 font-mono">{selected.at?.slice(0,16)}</div>
                  <p className="whitespace-pre-wrap leading-relaxed">{selected.body}</p>
                  <AttachmentList files={selected.attachments || []} testIdPrefix={`msg-${selected.id}-att`} />
                </div>
              ) : <div className="text-muted-foreground text-center pt-16"><Mail className="w-8 h-8 mx-auto mb-2 opacity-50"/>{t("inbox")}</div>}
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
